import { useEffect, useMemo, useRef, useState } from "react";
import {
  useAutomationKPIDashboard,
  useGetFieldsFromLatestAutomationDataRecord,
} from "../services/AutomationRecordServices";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  Assessment as AssessmentIcon,
  AutoMode as AutoModeIcon,
  HighlightAlt as DragZoomIcon,
  PictureAsPdf as PdfIcon,
  RateReview as RateReviewIcon,
  Refresh as RefreshIcon,
  Verified as VerifiedIcon,
  VerifiedUser as VerifiedUserIcon,
  ZoomOutMap as ZoomOutMapIcon,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import LoadingOverlay from "react-loading-overlay-ts";
import { useReactToPrint } from "react-to-print";
import {
  Area,
  BarChart,
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { RecordFilter } from "src/reusable/components/RecordFilter";
import { Loading } from "src/reusable/LoadingSpinner";
import { initialWhereFilterValue } from "src/reusable/WhereFilter";
import InfoTooltip from "src/reusable/components/InfoTooltip";
import { AutomationConfigTemplateHeader } from "../CapAutoComponents";
import { ConfusionMatrixSection } from "./ConfusionMatrix";
import { KPI_TOOLTIPS } from "./tooltipConstants";
import useChartZoom from "./useChartZoom";

// --- Chart palette (Tailwind-inspired, modern SaaS tones) ---
// Muted, harmonious colors that avoid the raw Material Design look.
const CHART_COLORS = {
  automated: "#10b981", // emerald-500 — positive outcome
  manualSample: "#6366f1", // indigo-500 — intentional action
  manualReview: "#94a3b8", // slate-400 — muted neutral
  critical: "#2563eb", // blue-600 — primary data series
  nonCritical: "#8b5cf6", // violet-500 — complementary series
};

// --- Time period options ---
const TIME_PERIODS = [
  { label: "Last 30 days", days: 30, timeBucket: "day" },
  { label: "Last 90 days", days: 90, timeBucket: "week" },
  { label: "Last 6 months", days: 180, timeBucket: "week" },
  { label: "Last year", days: 365, timeBucket: "month" },
  { label: "All time", days: null, timeBucket: "month" },
  { label: "Custom", isCustom: true },
];
// Default landing view: All time — gives new visitors the full historical
// picture; narrower windows are one dropdown click away.
const DEFAULT_PERIOD_IDX = TIME_PERIODS.findIndex(
  (p) => p.label === "All time",
);

// Choose a sensible bucket for a custom range so points don't clump or scatter
const bucketForRange = (startDate, endDate) => {
  if (!startDate || !endDate) return "day";
  const days =
    (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24);
  if (days <= 45) return "day";
  if (days <= 180) return "week";
  return "month";
};

// --- Accuracy chart segments ---
// Single chart with a tab selector — mirrors the confusion matrix tabs below.
// Only critical accuracy is surfaced; the backend exposes the matching per-
// period, rolling, and cumulative (from record_series) field per segment.
const ACCURACY_SEGMENTS = [
  {
    key: "all",
    label: "All Records",
    perPeriod: "critical_accuracy",
    rolling: "rolling_critical_accuracy",
    cumulative: "cumulative_critical_accuracy",
  },
  {
    key: "automated",
    label: "Automated",
    perPeriod: "automated_critical_accuracy",
    rolling: "rolling_automated_critical_accuracy",
    cumulative: "cumulative_automated_critical_accuracy",
  },
  {
    key: "manual_sample",
    label: "Manual Samples",
    perPeriod: "manual_sample_critical_accuracy",
    rolling: "rolling_manual_sample_critical_accuracy",
    cumulative: "cumulative_manual_sample_critical_accuracy",
  },
];

const getDateRange = (days) => {
  if (!days) return {};
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  return {
    startDate: start.toISOString().split("T")[0],
    endDate: end.toISOString().split("T")[0],
  };
};

const formatPct = (v) => (v != null ? `${(v * 100).toFixed(1)}%` : "N/A");

// Compact record-count labels for the "over records" x-axis (e.g. 1.2k, 45k, 2.1M).
const formatRecordCount = (n) => {
  if (n == null) return "";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return `${n}`;
};

// --- StatCard (matches Management Console pattern) ---
const StatCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  color = "primary",
  tooltip,
}) => (
  <Card
    variant="outlined"
    data-testid={`cap-auto-kpis-stat-${title.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <CardContent>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: `${color}.lighter`,
            color: `${color}.main`,
          }}
        >
          <Icon fontSize="large" />
        </Box>
        <Box>
          <Typography
            variant="h4"
            fontWeight="bold"
            data-testid={`cap-auto-kpis-stat-value-${title.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {typeof value === "number" ? value.toLocaleString() : value}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            {tooltip && <InfoTooltip title={tooltip} />}
          </Box>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  subtitle: PropTypes.string,
  icon: PropTypes.elementType.isRequired,
  color: PropTypes.string,
  tooltip: PropTypes.string,
};

// Rendered when the API response has no record_series (older backend that
// doesn't yet return the field, or a template with zero records). Keeps the
// chart slot the same height so the Grid pairing stays aligned.
const RecordSeriesEmptyState = () => (
  <Box
    sx={{
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "text.secondary",
    }}
    data-testid="cap-auto-kpis-record-series-empty"
  >
    <Typography variant="body2" sx={{ textAlign: "center", px: 2 }}>
      Record-level curve unavailable.
      <br />
      Restart the backend to pick up the latest API response shape.
    </Typography>
  </Box>
);

// --- Chart header with inline metric chips ---
const ChartHeader = ({ title, metrics, tooltip, children }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 1,
      mb: 1,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Typography variant="h6">{title}</Typography>
      {tooltip && <InfoTooltip title={tooltip} />}
    </Box>
    {metrics.map(
      (m) =>
        m.value != null && (
          <Chip
            key={m.label}
            label={`${m.label}: ${m.value}`}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 500, color: m.color || "text.secondary" }}
          />
        ),
    )}
    {children}
  </Box>
);

ChartHeader.propTypes = {
  title: PropTypes.string.isRequired,
  metrics: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string,
      color: PropTypes.string,
    }),
  ).isRequired,
  tooltip: PropTypes.string,
  children: PropTypes.node,
};

// Custom Recharts legend that reflects each line's strokeDasharray so the
// reader can tell rolling (solid) from per-period (dashed) without hovering.
// Recharts' default legend icon ignores dash patterns and both entries end up
// looking identical when the lines share a color.
const DashAwareLegend = ({ payload }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      gap: 3,
      flexWrap: "wrap",
      mt: 1,
    }}
  >
    {payload?.map((entry) => {
      const dash = entry.payload?.strokeDasharray;
      return (
        <Box
          key={entry.value}
          sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
        >
          <svg width="28" height="10">
            <line
              x1="0"
              y1="5"
              x2="28"
              y2="5"
              stroke={entry.color}
              strokeWidth={dash ? 1.5 : 2.5}
              strokeDasharray={dash || undefined}
            />
          </svg>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {entry.value}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

DashAwareLegend.propTypes = {
  payload: PropTypes.array,
};

const formatTs = (ts, timeBucket) => {
  try {
    const d = new Date(ts);
    if (timeBucket === "month" || timeBucket === "quarter") {
      return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
    }
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "";
  }
};

// Top-right chart overlay: idle shows a passive "drag to zoom" hint,
// zoomed swaps it for the reset button in the same slot.
const ZoomResetOverlay = ({ isZoomed, onReset }) =>
  isZoomed ? (
    <Button
      size="small"
      variant="outlined"
      onClick={onReset}
      startIcon={<ZoomOutMapIcon sx={{ fontSize: 14 }} />}
      data-testid="kpi-chart-zoom-reset"
      sx={{
        position: "absolute",
        top: 4,
        right: 4,
        zIndex: 1,
        bgcolor: "background.paper",
        textTransform: "none",
        fontSize: "0.75rem",
        py: 0.25,
        px: 1,
      }}
    >
      Reset zoom
    </Button>
  ) : (
    <Typography
      variant="caption"
      data-testid="kpi-chart-zoom-hint"
      sx={{
        position: "absolute",
        top: 4,
        right: 4,
        zIndex: 1,
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 0.75,
        py: 0.25,
        borderRadius: 1,
        color: "text.secondary",
        bgcolor: "rgba(255, 255, 255, 0.72)",
        // Purely informational — let drags start through the text.
        pointerEvents: "none",
      }}
    >
      <DragZoomIcon sx={{ fontSize: 14 }} />
      drag to zoom
    </Typography>
  );

ZoomResetOverlay.propTypes = {
  isZoomed: PropTypes.bool,
  onReset: PropTypes.func,
};

const KPIRateChart = ({
  title,
  metrics,
  dataKey,
  rollingDataKey,
  name,
  color,
  data,
  timeBucket,
  tooltip,
}) => {
  const zoom = useChartZoom();

  useEffect(() => {
    zoom.reset();
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <ChartHeader title={title} metrics={metrics} tooltip={tooltip} />
      <Box
        sx={{
          height: 250,
          cursor: "crosshair",
          userSelect: "none",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <ZoomResetOverlay isZoomed={zoom.isZoomed} onReset={zoom.reset} />
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={data}
            onMouseDown={zoom.onMouseDown}
            onMouseMove={zoom.onMouseMove}
            onMouseUp={zoom.onMouseUp}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="_ts"
              type="number"
              domain={zoom.domain || ["dataMin", "dataMax"]}
              allowDataOverflow
              tickFormatter={(v) => formatTs(v, timeBucket)}
              stroke="#94a3b8"
            />
            <YAxis
              domain={[0, 1]}
              tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
              stroke="#94a3b8"
            />
            <Tooltip
              labelFormatter={(v) => formatTs(v, timeBucket)}
              formatter={(v) => formatPct(v)}
            />
            {rollingDataKey && <Legend content={<DashAwareLegend />} />}
            <Area
              type="monotone"
              dataKey={rollingDataKey ?? dataKey}
              name={rollingDataKey ? `${name} (rolling avg)` : name}
              stroke={color}
              fill={color}
              fillOpacity={rollingDataKey ? 0.2 : 0.3}
              strokeWidth={rollingDataKey ? 2.5 : 2}
            />
            {rollingDataKey && (
              <Line
                type="monotone"
                dataKey={dataKey}
                name={`${name} (per period)`}
                stroke={color}
                strokeWidth={1.5}
                strokeDasharray="4 3"
                dot={false}
                connectNulls
              />
            )}
            {zoom.selectionArea}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

KPIRateChart.propTypes = {
  title: PropTypes.string.isRequired,
  metrics: PropTypes.array.isRequired,
  dataKey: PropTypes.string.isRequired,
  rollingDataKey: PropTypes.string,
  name: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  timeBucket: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
};

// --- Per-field metric row ---
const MetricRow = ({ label, rate, accuracy, truthTotal }) => {
  const hasRate = rate != null;
  const hasAcc = accuracy != null && truthTotal > 0;
  const accPct = hasAcc ? (accuracy * 100).toFixed(1) : null;
  const ratePct = hasRate ? (rate * 100).toFixed(1) : null;

  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 2 }}>
        {hasRate && (
          <Typography variant="body2">
            Rate:{" "}
            <Box component="span" fontWeight="bold">
              {ratePct}%
            </Box>
          </Typography>
        )}
        <Typography variant="body2">
          Accuracy:{" "}
          <Box component="span" fontWeight="bold">
            {hasAcc ? `${accPct}%` : "—"}
          </Box>
        </Typography>
        <Typography variant="body2" color="text.disabled">
          {hasAcc ? `${truthTotal.toLocaleString()} verified` : ""}
        </Typography>
      </Box>
    </Box>
  );
};

MetricRow.propTypes = {
  label: PropTypes.string.isRequired,
  rate: PropTypes.number,
  accuracy: PropTypes.number,
  truthTotal: PropTypes.number,
};

const PerFieldMetricsSection = ({ metrics }) => {
  if (!metrics || metrics.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Typography variant="h6">Per-Field Breakdown</Typography>
        <InfoTooltip title={KPI_TOOLTIPS.perFieldBreakdown} />
      </Box>
      <Grid container spacing={2}>
        {metrics.map((field) => (
          <Grid item xs={12} md={6} key={field.field_name}>
            <Card variant="outlined">
              <CardContent sx={{ "&:last-child": { pb: 1.5 } }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {field.field_name}
                </Typography>
                <Box sx={{ display: "flex", gap: 2, mt: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {field.total.toLocaleString()} total predictions
                  </Typography>
                  {field.per_label_threshold ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontStyle: "italic" }}
                    >
                      Per-label thresholds (see autoflow config)
                    </Typography>
                  ) : field.threshold != null ? (
                    <Typography variant="caption" color="text.secondary">
                      Threshold:{" "}
                      <Box component="span" fontWeight="bold">
                        {(field.threshold * 100).toFixed(1)}%
                      </Box>
                    </Typography>
                  ) : null}
                  {field.avg_confidence != null && (
                    <Typography variant="caption" color="text.secondary">
                      Avg Confidence:{" "}
                      <Box component="span" fontWeight="bold">
                        {(field.avg_confidence * 100).toFixed(1)}%
                      </Box>
                    </Typography>
                  )}
                </Box>
                <Divider sx={{ my: 1 }} />
                <MetricRow
                  label="Above Threshold"
                  rate={field.automated_rate}
                  accuracy={field.automated_accuracy}
                  truthTotal={field.automated_truth_total}
                />
                <MetricRow
                  label="Overall"
                  accuracy={field.overall_accuracy}
                  truthTotal={field.overall_truth_total}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

PerFieldMetricsSection.propTypes = {
  metrics: PropTypes.arrayOf(PropTypes.object),
};

export const CAPAutomationStats = (props) => {
  const automationConfigTemplateId =
    props.match.params.automation_config_template_id;

  const [periodIdx, setPeriodIdx] = useState(DEFAULT_PERIOD_IDX);
  // Separate segment selectors for the two accuracy charts — switching
  // segments on one should not drag the other along.
  const [timeAccuracySegmentIdx, setTimeAccuracySegmentIdx] = useState(0);
  const [recordAccuracySegmentIdx, setRecordAccuracySegmentIdx] = useState(0);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [whereFilters, setWhereFilters] = useState([]);
  const [tempWhereFilters, setTempWhereFilters] = useState([
    initialWhereFilterValue,
  ]);
  const printRef = useRef(null);

  const timeAccuracySegment = ACCURACY_SEGMENTS[timeAccuracySegmentIdx];
  const recordAccuracySegment = ACCURACY_SEGMENTS[recordAccuracySegmentIdx];

  const selectedPeriod = TIME_PERIODS[periodIdx];
  // Custom mode is only queryable once both dates are set; otherwise we'd fire
  // an all-time/day-bucketed request that could be huge and slow.
  const customRangeReady =
    !selectedPeriod.isCustom || (!!customStart && !!customEnd);
  // Preserve the last queryable dateRange while the user is picking custom
  // dates, so the query key stays stable and the chart axis formatter keeps
  // using the previously-applied timeBucket (no transient mis-format).
  const lastDateRangeRef = useRef({
    ...getDateRange(TIME_PERIODS[DEFAULT_PERIOD_IDX].days),
    timeBucket: TIME_PERIODS[DEFAULT_PERIOD_IDX].timeBucket,
  });
  const dateRange = useMemo(() => {
    if (!customRangeReady) return lastDateRangeRef.current;
    const next = selectedPeriod.isCustom
      ? {
          startDate: customStart,
          endDate: customEnd,
          timeBucket: bucketForRange(customStart, customEnd),
        }
      : {
          ...getDateRange(selectedPeriod.days),
          timeBucket: selectedPeriod.timeBucket,
        };
    lastDateRangeRef.current = next;
    return next;
  }, [selectedPeriod, customStart, customEnd, customRangeReady]);

  const {
    data: dashboardData,
    isLoading,
    isError,
    error,
    isFetching,
    refetch,
  } = useAutomationKPIDashboard(
    automationConfigTemplateId,
    dateRange,
    whereFilters,
    true,
    customRangeReady,
  );

  const summary = dashboardData?.summary;
  const timeSeries = useMemo(() => {
    const series = dashboardData?.time_series || [];
    return series.map((row) => ({
      ...row,
      _ts: new Date(row.period + "T00:00:00").getTime(),
      manual_review_count:
        (row.total_records ?? 0) -
        (row.automated_count ?? 0) -
        (row.manual_sample_count ?? 0),
    }));
  }, [dashboardData]);
  const recordSeries = dashboardData?.record_series || [];
  const latestRecordPoint = recordSeries[recordSeries.length - 1];
  const confusionMatrices = dashboardData?.confusion_matrices;
  const perFieldMetrics = dashboardData?.per_field_metrics;

  const latest = useMemo(() => {
    if (!timeSeries.length) return null;
    return timeSeries[timeSeries.length - 1];
  }, [timeSeries]);

  const autoRecordsZoom = useChartZoom();
  const accuracyTimeZoom = useChartZoom();
  const accuracyRecordsZoom = useChartZoom();

  useEffect(() => {
    autoRecordsZoom.reset();
    accuracyRecordsZoom.reset();
  }, [recordSeries]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    accuracyTimeZoom.reset();
  }, [timeSeries]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: fields } = useGetFieldsFromLatestAutomationDataRecord(
    automationConfigTemplateId,
  );

  const resetFilters = () => {
    setTempWhereFilters([initialWhereFilterValue]);
    setWhereFilters([]);
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Automation-KPIs-${new Date().toISOString().split("T")[0]}`,
    pageStyle: `
      @page { size: A4 landscape; margin: 10mm; }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .no-print { display: none !important; }
      }
    `,
  });

  const hasSummary = summary && summary.total_records > 0;
  // Use dateRange.timeBucket so Custom ranges (where selectedPeriod has no
  // timeBucket) still get the correct axis/tooltip formatting.
  const timeBucket = dateRange.timeBucket;

  return (
    <Box sx={{ p: 3 }} data-testid="cap-auto-kpis-container">
      {/* Header */}
      <AutomationConfigTemplateHeader
        title="Automation KPIs"
        automationConfigTemplateId={automationConfigTemplateId}
        selectedButton="kpis"
        testIdPrefix="cap-auto-kpis"
      />

      {/* Controls row */}
      <Box
        className="no-print"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mt: 1,
          mb: 2,
        }}
      >
        <RecordFilter
          whereFilters={whereFilters}
          setWhereFilters={setWhereFilters}
          id={automationConfigTemplateId}
          group="automation_records"
          fields={fields}
          tempWhereFilters={tempWhereFilters}
          setTempWhereFilters={setTempWhereFilters}
          isFetching={isFetching}
          resetFilters={resetFilters}
          testIdPrefix="cap-auto-kpis"
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={periodIdx}
              label="Time Period"
              onChange={(e) => setPeriodIdx(e.target.value)}
              data-testid="cap-auto-kpis-time-period"
              data-attr="cap-ai:select:kpis-time-period"
            >
              {TIME_PERIODS.map((p, i) => (
                <MenuItem key={p.label} value={i}>
                  {p.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedPeriod.isCustom && (
            <>
              <TextField
                size="small"
                type="date"
                label="From"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: customEnd || undefined }}
                data-testid="cap-auto-kpis-custom-start"
              />
              <TextField
                size="small"
                type="date"
                label="To"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: customStart || undefined }}
                data-testid="cap-auto-kpis-custom-end"
              />
            </>
          )}
          <Button
            variant="outlined"
            size="small"
            onClick={() => refetch()}
            startIcon={<RefreshIcon />}
            data-testid="cap-auto-kpis-btn-refresh"
            data-attr="cap-ai:btn:refresh-kpis"
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handlePrint}
            startIcon={<PdfIcon />}
            data-testid="cap-auto-kpis-btn-export-pdf"
            data-attr="cap-ai:btn:export-kpis-pdf"
          >
            Export PDF
          </Button>
        </Box>
      </Box>

      {isError && (
        <Alert severity="error">{error?.response?.data?.detail}</Alert>
      )}

      <LoadingOverlay
        active={isLoading}
        spinner={
          <div data-testid="cap-auto-kpis-loading">
            <Loading height={"75em"} width={"75em"} />
          </div>
        }
        text="Loading KPIs..."
      >
        <Box ref={printRef}>
          {hasSummary ? (
            <>
              {/* Summary stat cards */}
              <Grid
                container
                spacing={2}
                sx={{ mb: 3 }}
                data-testid="cap-auto-kpis-dashboard"
              >
                <Grid item xs={12} sm={6} md>
                  <StatCard
                    title="Total Records"
                    value={summary.total_records}
                    icon={AssessmentIcon}
                    color="primary"
                    tooltip={KPI_TOOLTIPS.totalRecords}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md>
                  <StatCard
                    title="Automation Rate"
                    value={formatPct(summary.automated_pct)}
                    subtitle={`${(summary.automated_count ?? 0).toLocaleString()} automated`}
                    icon={AutoModeIcon}
                    color="success"
                    tooltip={KPI_TOOLTIPS.automationRate}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md>
                  <StatCard
                    title="Manual Sample Rate"
                    value={formatPct(
                      summary.total_records > 0
                        ? (summary.manual_sample_count ?? 0) /
                            summary.total_records
                        : 0,
                    )}
                    subtitle={`${(summary.manual_sample_count ?? 0).toLocaleString()} sampled`}
                    icon={RateReviewIcon}
                    color="primary"
                    tooltip={KPI_TOOLTIPS.manualSampleRate}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md>
                  <StatCard
                    title="Critical Accuracy"
                    value={formatPct(summary.critical_accuracy)}
                    icon={VerifiedIcon}
                    color="info"
                    tooltip={KPI_TOOLTIPS.criticalAccuracy}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md>
                  <StatCard
                    title="Truth Received"
                    value={formatPct(summary.truth_received_pct)}
                    subtitle={`${(summary.truth_received_count ?? 0).toLocaleString()} of ${(summary.total_records ?? 0).toLocaleString()}`}
                    icon={VerifiedUserIcon}
                    color="secondary"
                    tooltip={KPI_TOOLTIPS.truthReceived}
                  />
                </Grid>
              </Grid>

              {/* Charts */}
              {timeSeries.length > 0 && (
                <>
                  {/* Volume Over Time (unique — BarChart with stacked bars) */}
                  <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <ChartHeader
                      title="Record Volume Over Time"
                      tooltip={KPI_TOOLTIPS.recordVolume}
                      metrics={[
                        {
                          label: "Latest Period",
                          value: latest?.total_records?.toLocaleString(),
                        },
                        {
                          label: "Automated",
                          value: latest?.automated_count?.toLocaleString(),
                          color: CHART_COLORS.automated,
                        },
                        {
                          label: "Manual Sample",
                          value: latest?.manual_sample_count?.toLocaleString(),
                          color: CHART_COLORS.manualSample,
                        },
                        {
                          label: "Manual Review",
                          value: latest?.manual_review_count?.toLocaleString(),
                          color: CHART_COLORS.manualReview,
                        },
                      ]}
                    />
                    <Box sx={{ height: 300, overflow: "hidden" }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={timeSeries} maxBarSize={48}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#e2e8f0"
                          />
                          <XAxis
                            dataKey="period"
                            tickFormatter={(v) =>
                              formatTs(
                                new Date(v + "T00:00:00").getTime(),
                                timeBucket,
                              )
                            }
                            stroke="#94a3b8"
                          />
                          <YAxis stroke="#94a3b8" />
                          <Tooltip
                            labelFormatter={(v) =>
                              formatTs(
                                new Date(v + "T00:00:00").getTime(),
                                timeBucket,
                              )
                            }
                          />
                          <Legend />
                          <Bar
                            dataKey="automated_count"
                            name="Automated"
                            fill={CHART_COLORS.automated}
                            stackId="a"
                          />
                          <Bar
                            dataKey="manual_sample_count"
                            name="Manual Sample"
                            fill={CHART_COLORS.manualSample}
                            stackId="a"
                          />
                          <Bar
                            dataKey="manual_review_count"
                            name="Manual Review"
                            fill={CHART_COLORS.manualReview}
                            stackId="a"
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>

                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={6}>
                      <KPIRateChart
                        title="Automation Rate Over Time"
                        tooltip={KPI_TOOLTIPS.automationRateOverTime}
                        metrics={[
                          {
                            label: "Latest",
                            value: formatPct(latest?.rolling_automated_pct),
                            color: CHART_COLORS.automated,
                          },
                        ]}
                        dataKey="automated_pct"
                        rollingDataKey="rolling_automated_pct"
                        name="Automation Rate"
                        color={CHART_COLORS.automated}
                        data={timeSeries}
                        timeBucket={timeBucket}
                      />
                    </Grid>

                    {/* Automation Rate over cumulative records — smooths out
                        bursty time periods and shows the trajectory as a
                        function of volume processed. */}
                    <Grid item xs={12} md={6}>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2 }}
                        data-testid="cap-auto-kpis-automation-rate-over-records"
                      >
                        <ChartHeader
                          title="Automation Rate Over Records"
                          tooltip={KPI_TOOLTIPS.automationRateOverRecords}
                          metrics={[
                            {
                              label: "Cumulative",
                              value: formatPct(
                                latestRecordPoint?.cumulative_automated_pct,
                              ),
                              color: CHART_COLORS.automated,
                            },
                            {
                              label: "Records",
                              value:
                                latestRecordPoint?.record_index?.toLocaleString(),
                            },
                          ]}
                        />
                        <Box
                          sx={{
                            height: 250,
                            cursor: "crosshair",
                            userSelect: "none",
                            position: "relative",
                          }}
                        >
                          <ZoomResetOverlay
                            isZoomed={autoRecordsZoom.isZoomed}
                            onReset={autoRecordsZoom.reset}
                          />
                          {recordSeries.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart
                                data={recordSeries}
                                onMouseDown={autoRecordsZoom.onMouseDown}
                                onMouseMove={autoRecordsZoom.onMouseMove}
                                onMouseUp={autoRecordsZoom.onMouseUp}
                              >
                                <CartesianGrid
                                  strokeDasharray="3 3"
                                  stroke="#e2e8f0"
                                />
                                <XAxis
                                  dataKey="record_index"
                                  type="number"
                                  domain={
                                    autoRecordsZoom.domain || [
                                      "dataMin",
                                      "dataMax",
                                    ]
                                  }
                                  allowDataOverflow
                                  tickFormatter={formatRecordCount}
                                  stroke="#94a3b8"
                                />
                                <YAxis
                                  domain={[0, 1]}
                                  tickFormatter={(v) =>
                                    `${(v * 100).toFixed(0)}%`
                                  }
                                  stroke="#94a3b8"
                                />
                                <Tooltip
                                  labelFormatter={(v) =>
                                    `${Number(v).toLocaleString()} records`
                                  }
                                  formatter={(v) => formatPct(v)}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="cumulative_automated_pct"
                                  name="Automation Rate (cumulative)"
                                  stroke={CHART_COLORS.automated}
                                  strokeWidth={2.5}
                                  connectNulls
                                  dot={false}
                                />
                                {autoRecordsZoom.selectionArea}
                              </LineChart>
                            </ResponsiveContainer>
                          ) : (
                            <RecordSeriesEmptyState />
                          )}
                        </Box>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 2,
                            mb: 1,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant="h6">
                              Accuracy Over Time
                            </Typography>
                            <InfoTooltip
                              title={KPI_TOOLTIPS.accuracyOverTime}
                            />
                          </Box>
                          <Tabs
                            value={timeAccuracySegmentIdx}
                            onChange={(_, v) => setTimeAccuracySegmentIdx(v)}
                            sx={{ minHeight: 32 }}
                            data-testid="cap-auto-kpis-accuracy-segments"
                          >
                            {ACCURACY_SEGMENTS.map((seg) => (
                              <Tab
                                key={seg.key}
                                label={seg.label}
                                sx={{ minHeight: 32, py: 0.5 }}
                                data-testid={`cap-auto-kpis-accuracy-segment-${seg.key}`}
                              />
                            ))}
                          </Tabs>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            flexWrap: "wrap",
                            mb: 1,
                          }}
                        >
                          <Chip
                            label={`Critical: ${formatPct(
                              latest?.[timeAccuracySegment.rolling],
                            )}`}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontWeight: 500,
                              color: CHART_COLORS.critical,
                            }}
                          />
                        </Box>
                        <Box
                          sx={{
                            height: 300,
                            cursor: "crosshair",
                            userSelect: "none",
                            position: "relative",
                          }}
                        >
                          <ZoomResetOverlay
                            isZoomed={accuracyTimeZoom.isZoomed}
                            onReset={accuracyTimeZoom.reset}
                          />
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart
                              data={timeSeries}
                              onMouseDown={accuracyTimeZoom.onMouseDown}
                              onMouseMove={accuracyTimeZoom.onMouseMove}
                              onMouseUp={accuracyTimeZoom.onMouseUp}
                            >
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#e2e8f0"
                              />
                              <XAxis
                                dataKey="_ts"
                                type="number"
                                domain={
                                  accuracyTimeZoom.domain || [
                                    "dataMin",
                                    "dataMax",
                                  ]
                                }
                                allowDataOverflow
                                tickFormatter={(v) => formatTs(v, timeBucket)}
                                stroke="#94a3b8"
                              />
                              <YAxis
                                domain={[0, 1]}
                                tickFormatter={(v) =>
                                  `${(v * 100).toFixed(0)}%`
                                }
                                stroke="#94a3b8"
                              />
                              <Tooltip
                                labelFormatter={(v) => formatTs(v, timeBucket)}
                                formatter={(v) => formatPct(v)}
                              />
                              <Legend content={<DashAwareLegend />} />
                              <Area
                                type="monotone"
                                dataKey={timeAccuracySegment.rolling}
                                name="Critical (rolling avg)"
                                stroke={CHART_COLORS.critical}
                                fill={CHART_COLORS.critical}
                                fillOpacity={0.2}
                                strokeWidth={2.5}
                                connectNulls
                              />
                              <Line
                                type="monotone"
                                dataKey={timeAccuracySegment.perPeriod}
                                name="Critical (per period)"
                                stroke={CHART_COLORS.critical}
                                strokeWidth={1.5}
                                strokeDasharray="4 3"
                                connectNulls
                                dot={false}
                              />
                              {accuracyTimeZoom.selectionArea}
                            </ComposedChart>
                          </ResponsiveContainer>
                        </Box>
                      </Paper>
                    </Grid>

                    {/* Accuracy over cumulative records — same segment tabs as
                        the time-based chart; cumulative (weighted) values let
                        the eye compare trajectory as volume grows. */}
                    <Grid item xs={12} md={6}>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2 }}
                        data-testid="cap-auto-kpis-accuracy-over-records"
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 2,
                            mb: 1,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant="h6">
                              Accuracy Over Records
                            </Typography>
                            <InfoTooltip
                              title={KPI_TOOLTIPS.accuracyOverRecords}
                            />
                          </Box>
                          <Tabs
                            value={recordAccuracySegmentIdx}
                            onChange={(_, v) => setRecordAccuracySegmentIdx(v)}
                            sx={{ minHeight: 32 }}
                            data-testid="cap-auto-kpis-accuracy-over-records-segments"
                          >
                            {ACCURACY_SEGMENTS.map((seg) => (
                              <Tab
                                key={seg.key}
                                label={seg.label}
                                sx={{ minHeight: 32, py: 0.5 }}
                                data-testid={`cap-auto-kpis-accuracy-over-records-segment-${seg.key}`}
                              />
                            ))}
                          </Tabs>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            flexWrap: "wrap",
                            mb: 1,
                          }}
                        >
                          <Chip
                            label={`Critical: ${formatPct(
                              latestRecordPoint?.[
                                recordAccuracySegment.cumulative
                              ],
                            )}`}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontWeight: 500,
                              color: CHART_COLORS.critical,
                            }}
                          />
                        </Box>
                        <Box
                          sx={{
                            height: 300,
                            cursor: "crosshair",
                            userSelect: "none",
                            position: "relative",
                          }}
                        >
                          <ZoomResetOverlay
                            isZoomed={accuracyRecordsZoom.isZoomed}
                            onReset={accuracyRecordsZoom.reset}
                          />
                          {recordSeries.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart
                                data={recordSeries}
                                onMouseDown={accuracyRecordsZoom.onMouseDown}
                                onMouseMove={accuracyRecordsZoom.onMouseMove}
                                onMouseUp={accuracyRecordsZoom.onMouseUp}
                              >
                                <CartesianGrid
                                  strokeDasharray="3 3"
                                  stroke="#e2e8f0"
                                />
                                <XAxis
                                  dataKey="record_index"
                                  type="number"
                                  domain={
                                    accuracyRecordsZoom.domain || [
                                      "dataMin",
                                      "dataMax",
                                    ]
                                  }
                                  allowDataOverflow
                                  tickFormatter={formatRecordCount}
                                  stroke="#94a3b8"
                                />
                                <YAxis
                                  domain={[0, 1]}
                                  tickFormatter={(v) =>
                                    `${(v * 100).toFixed(0)}%`
                                  }
                                  stroke="#94a3b8"
                                />
                                <Tooltip
                                  labelFormatter={(v) =>
                                    `${Number(v).toLocaleString()} records`
                                  }
                                  formatter={(v) => formatPct(v)}
                                />
                                <Legend />
                                <Line
                                  type="monotone"
                                  dataKey={recordAccuracySegment.cumulative}
                                  name="Critical (cumulative)"
                                  stroke={CHART_COLORS.critical}
                                  strokeWidth={2.5}
                                  connectNulls
                                  dot={false}
                                />
                                {accuracyRecordsZoom.selectionArea}
                              </LineChart>
                            </ResponsiveContainer>
                          ) : (
                            <RecordSeriesEmptyState />
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </>
              )}

              {/* Per-Field Breakdown */}
              <PerFieldMetricsSection metrics={perFieldMetrics} />

              {/* Confusion Matrices */}
              {confusionMatrices && (
                <ConfusionMatrixSection confusionMatrices={confusionMatrices} />
              )}
            </>
          ) : (
            <Alert severity="info" data-testid="cap-auto-kpis-empty-state">
              No KPIs found, run Autoflow to see data
            </Alert>
          )}
        </Box>
      </LoadingOverlay>
    </Box>
  );
};

CAPAutomationStats.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      automation_config_template_id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};
